// src/components/Sidebar.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  Divider,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Input,
  TextField,
  Snackbar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HistoryIcon from '@mui/icons-material/History';
import TodayIcon from '@mui/icons-material/Today';
import UpdateIcon from '@mui/icons-material/Update';
import ClearIcon from '@mui/icons-material/Clear';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useChatHistory } from '../hooks/useChatHistory';
import { useSearchParams } from 'react-router-dom';
import { AddCircleOutline, Search } from '@mui/icons-material';
import '../styles/DrawerToggle.css';
import { useChatContext, useRegisterHistoryRefresh } from '../context/ChatContext';
import { useI18n } from '../hooks/useI18n';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const formatRelativeTime = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// API function to update chat session title
const updateChatSessionTitle = async (
  sessionId: string,
  newTitle: string,
  token: string
): Promise<{ success: boolean; message: string; newTitle?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat/session/${sessionId}/title/${token}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title: newTitle }),
    });

    const data = await response.json();

    if (response.ok) {
      return {
        success: true,
        message: data.message || 'Title updated successfully',
        newTitle: data.new_title,
      };
    } else {
      return {
        success: false,
        message: data.error || 'Failed to update title',
      };
    }
  } catch (error) {
    console.error('Error updating chat session title:', error);
    return {
      success: false,
      message: 'Network error occurred while updating title',
    };
  }
};

interface ChatHistoryItem {
  session_id: string;
  session_title: string;
  last_ended_at: string;
  is_active?: boolean;
}

interface EditableChatItemProps {
  item: ChatHistoryItem;
  onClick: (sessionId: string) => void;
  onTitleUpdate: (sessionId: string, newTitle: string) => void;
  token: string;
  isActive?: boolean;
}

const EditableChatItem: React.FC<EditableChatItemProps> = ({
  item,
  onClick,
  onTitleUpdate,
  token,
  isActive = false,
}) => {
  const _t = useI18n();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(item.session_title);
  const [isUpdating, setIsUpdating] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const pressTimer = useRef<number | null>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const LONG_PRESS_DURATION = 750; // 750ms for a long press

  const handlePressStart = () => {
    // Clear any existing timer
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
    }
    // Start a new timer
    pressTimer.current = window.setTimeout(() => {
      // If the timer completes, it's a long press.
      setIsEditing(true);
      setEditedTitle(item.session_title);
      pressTimer.current = null; // Reset the timer ref
    }, LONG_PRESS_DURATION);
  };

  const handlePressEnd = () => {
    // If the press ends and the timer is still active, it was a short press (a click/tap).
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
      // Perform the standard click action for navigation.
      onClick(item.session_id);
    }
    // If pressTimer.current is null, the long press already triggered, so we do nothing.
  };

  const handlePressCancel = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  // Prevent context menu on long press
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Update editedTitle when item changes
  useEffect(() => {
    setEditedTitle(item.session_title);
  }, [item.session_title]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      const inputElement = inputRef.current.querySelector('input');
      if (inputElement) {
        inputElement.focus();
        inputElement.select();
      }
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (editedTitle.trim() === item.session_title.trim()) {
      setIsEditing(false);
      return;
    }

    if (!editedTitle.trim()) {
      setSnackbarMessage('Title cannot be empty');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
      return;
    }

    setIsUpdating(true);

    try {
      const result = await updateChatSessionTitle(item.session_id, editedTitle.trim(), token);

      if (result.success) {
        onTitleUpdate(item.session_id, result.newTitle || editedTitle.trim());
        setSnackbarMessage('Chat name updated successfully');
        setSnackbarSeverity('success');
        setShowSnackbar(true);
        setIsEditing(false);
      } else {
        setSnackbarMessage(result.message);
        setSnackbarSeverity('error');
        setShowSnackbar(true);
      }
    } catch (error) {
      console.error('Error in handleSave:', error);
      setSnackbarMessage('Failed to update chat name');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    }

    setIsUpdating(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedTitle(item.session_title);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <>
      <ListItem
        key={item.session_id}
        disablePadding
        sx={{ pl: 2, pr: 2 }}
        // onMouseEnter={() => setIsHovered(true)}
        // onMouseLeave={() => setIsHovered(false)}
      >
        {isEditing ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              px: 1,
              py: 0.5,
              gap: 1,
            }}
          >
            <TextField
              ref={inputRef}
              value={editedTitle}
              onChange={e => setEditedTitle(e.target.value)}
              onKeyDown={handleKeyPress}
              variant="outlined"
              size="small"
              fullWidth
              disabled={isUpdating}
              inputProps={{
                maxLength: 95,
                style: { fontSize: '0.875rem' },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handleSave}
                disabled={isUpdating}
                color="primary"
                sx={{ p: 0.5 }}
              >
                {isUpdating ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancel}
                disabled={isUpdating}
                color="secondary"
                sx={{ p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <ListItemButton
            selected={isActive}
            onClick={() => onClick(item.session_id)}
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressCancel} // To cancel if mouse leaves the button
            onTouchStart={handlePressStart}
            onTouchEnd={handlePressEnd}
            onTouchMove={handlePressCancel}
            onContextMenu={handleContextMenu}
            sx={{
              borderRadius: 1,
              mx: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <ListItemText
              primary={item.session_title}
              primaryTypographyProps={{
                variant: 'body2',
                noWrap: true,
                title: item.session_title,
              }}
              secondary={formatRelativeTime(item.last_ended_at)}
              secondaryTypographyProps={{ variant: 'caption' }}
              sx={{ minWidth: 0, flex: 1 }}
            />
            {/* {isHovered && (
              <IconButton
                size="small"
                onClick={handleEditClick}
                sx={{ 
                  p: 0.5,
                  ml: 1,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )} */}
          </ListItemButton>
        )}
      </ListItem>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setShowSnackbar(false)}
          sx={{ fontSize: '0.875rem' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default function Sidebar() {
  const t = useI18n();
  const [open, setOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const [isSearching, setSearching] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  // Refs for infinite scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);

  // Use the updated chat history hook with default values
  const hookResult = useChatHistory();

  const {
    history = { recent: [], older: [] },
    sessions = [],
    isLoading = false,
    error = null,
    refreshHistory = () => {},
    currentPage: _currentPage = 1,
    totalPages: _totalPages = 0,
    totalItems = 0,
    totalLoadedItems = 0,
    hasMorePages = false,
    loadMore = () => {},
    searchQuery = '',
    search = () => {},
  } = hookResult || {};

  const navigate = useNavigate();

  useRegisterHistoryRefresh(refreshHistory);

  const { triggerHistoryRefresh } = useChatContext();

  useEffect(() => {
    // Register a listener for history refresh events
    const unsubscribe = triggerHistoryRefresh.listen(() => {
      refreshHistory();
    });

    // Clean up the listener when the component unmounts
    return () => {
      unsubscribe();
    };
  }, [triggerHistoryRefresh, refreshHistory]);

  // Get current token and session ID
  const currentToken = searchParams.get('token') || '';
  const currentSessionId = searchParams.get('sessionId') || '';

  // Find active sessions - with default value
  const activeItems = sessions.filter(item => item.session_id === currentSessionId);

  // Handle title update
  const handleTitleUpdate = () => {
    // Refresh the history to get updated data
    refreshHistory();
  };

  // Handle search submission
  const handleSearch = () => {
    search(searchInput);
  };

  // Handle key press in search input
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchInput('');
    search('');
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMorePages) {
      return;
    }

    const setupObserver = () => {
      if (!loadingRef.current) {
        setTimeout(setupObserver, 100);
        return;
      }

      const observer = new IntersectionObserver(
        entries => {
          const target = entries[0];

          if (target.isIntersecting) {
            loadMore();
          }
        },
        {
          root: scrollContainerRef.current,
          rootMargin: '50px',
          threshold: 0.1,
        }
      );

      observer.observe(loadingRef.current);

      return () => {
        observer.disconnect();
      };
    };

    const cleanup = setupObserver();
    return cleanup;
  }, [loadMore, hasMorePages, totalLoadedItems]);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);

    // If opening the drawer, refresh the history
    if (newOpen) {
      refreshHistory();
    }
  };

  // const handleHistoryItemClick = (sessionId: string) => {
  //   const currentToken = searchParams.get('token');
  //   if (!currentToken) {
  //     console.error("Sidebar: Token is missing, cannot switch session.");
  //     return;
  //   }

  //   // Close drawer first to avoid accessibility warnings
  //   setOpen(false);

  //   // For new sessions (empty sessionId), remove sessionId from URL
  //   if (!sessionId || sessionId.trim() === '') {

  //     const urlParams = new URLSearchParams(window.location.search);
  //     const actualCurrentSessionId = urlParams.get('sessionId');

  //     if (actualCurrentSessionId) {

  //       navigate(`?token=${currentToken}`, { replace: true });
  //     } else {

  //       const newSessionParam = `newSession=${Date.now()}`;
  //       navigate(`?token=${currentToken}&${newSessionParam}`, { replace: true });

  //       // Remove the temporary parameter after a short delay to clean up the URL
  //       setTimeout(() => {
  //         const currentUrl = new URLSearchParams(window.location.search);
  //         if (currentUrl.has('newSession') && !currentUrl.has('sessionId')) {
  //           navigate(`?token=${currentToken}`, { replace: true });
  //         }
  //       }, 1000);
  //     }
  //   } else {
  //     navigate(`?token=${currentToken}&sessionId=${sessionId}`, { replace: true });
  //   }
  // };

  const handleHistoryItemClick = (sessionId: string) => {
    const currentToken = searchParams.get('token');
    const currentLocale = searchParams.get('locale');
    const currentVariant = searchParams.get('variant'); // Get current locale

    if (!currentToken) {
      console.error('Sidebar: Token is missing, cannot switch session.');
      return;
    }

    // Close drawer first to avoid accessibility warnings
    setOpen(false);

    // Helper function to build URL with preserved parameters
    const buildUrlParams = (additionalParams: Record<string, string> = {}) => {
      const params = new URLSearchParams();
      params.set('token', currentToken);

      // Preserve locale if it exists
      if (currentLocale) {
        params.set('locale', currentLocale);
      }

      if (currentVariant) {
        params.set('variant', currentVariant);
      }

      // Add any additional parameters
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        }
      });

      return params.toString();
    };

    // For new sessions (empty sessionId), remove sessionId from URL
    if (!sessionId || sessionId.trim() === '') {
      const urlParams = new URLSearchParams(window.location.search);
      const actualCurrentSessionId = urlParams.get('sessionId');

      if (actualCurrentSessionId) {
        // Navigate to new session without sessionId but with preserved locale
        navigate(`?${buildUrlParams()}`, { replace: true });
      } else {
        // Add temporary parameter for new session
        const newSessionParam = `newSession=${Date.now()}`;
        navigate(`?${buildUrlParams()}&${newSessionParam}`, { replace: true });

        // Remove the temporary parameter after a short delay to clean up the URL
        setTimeout(() => {
          const currentUrl = new URLSearchParams(window.location.search);
          if (currentUrl.has('newSession') && !currentUrl.has('sessionId')) {
            navigate(`?${buildUrlParams()}`, { replace: true });
          }
        }, 1000);
      }
    } else {
      // Navigate to existing session with preserved locale
      navigate(`?${buildUrlParams({ sessionId })}`, { replace: true });
    }
  };

  // Render list items for a specific group
  const renderHistoryGroup = (title: string, items: ChatHistoryItem[], icon: React.ReactNode) => {
    if (items.length === 0) {
      return null;
    }

    return (
      <React.Fragment key={title}>
        <ListItem sx={{ pb: 0, pt: 2 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {icon &&
              React.cloneElement(icon as React.ReactElement<Record<string, unknown>>, {
                sx: { mr: 1, fontSize: '1rem' },
              })}
            {title}
          </Typography>
        </ListItem>
        {items.map(item => {
          return (
            <EditableChatItem
              key={item.session_id}
              item={item}
              onClick={handleHistoryItemClick}
              onTitleUpdate={handleTitleUpdate}
              token={currentToken}
              isActive={item.session_id === currentSessionId}
            />
          );
        })}
        <Divider sx={{ mt: 1 }} />
      </React.Fragment>
    );
  };

  // Content inside the Drawer
  const DrawerList = (
    <Box sx={{ width: 280, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header Section - Fixed */}
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t.get('chatHistory')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              color="primary"
              aria-label="search chats"
              size="small"
              sx={{
                ':focus': { outline: 'none' },
                ':hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => setSearching(prev => !prev)}
            >
              <Search fontSize="small" />
            </IconButton>
            <IconButton
              color="primary"
              aria-label="add a chat"
              size="small"
              sx={{
                ':focus': { outline: 'none' },
                ':hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => handleHistoryItemClick('')}
            >
              <AddCircleOutline fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Search Section */}
        {isSearching && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Input
                placeholder="Search conversations..."
                fullWidth
                size="small"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                endAdornment={
                  searchInput && (
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }
                sx={{
                  fontSize: '0.875rem',
                  '&:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.12)' },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
                  },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSearch}
                disabled={isLoading}
                sx={{
                  ml: 1,
                  minWidth: '60px',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                }}
              >
                {isLoading ? '...' : 'Search'}
              </Button>
            </Box>

            {/* Search status */}
            {searchQuery && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {totalLoadedItems} of {totalItems} loaded
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Divider />

        {/* Active conversation (ongoing) */}
        {activeItems.length > 0 && (
          <>
            <ListItem sx={{ pt: 2 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}
              >
                <HistoryIcon sx={{ mr: 1, fontSize: '1rem' }} />
                {t.get('activeConversation')}
              </Typography>
            </ListItem>
            {activeItems.map(item => (
              <EditableChatItem
                key={item.session_id}
                item={item}
                onClick={handleHistoryItemClick}
                onTitleUpdate={handleTitleUpdate}
                token={currentToken}
                isActive={item.session_id === currentSessionId}
              />
            ))}
            <Divider sx={{ mt: 1 }} />
          </>
        )}
      </Box>

      {/* Scrollable Content Area */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0,0,0,0.3)',
          },
        }}
      >
        {error && (
          <Alert severity="error" sx={{ m: 1 }}>
            Error loading history.
            <Button size="small" onClick={refreshHistory} sx={{ ml: 1 }}>
              Retry
            </Button>
          </Alert>
        )}

        {/* Initial loading state */}
        {isLoading && totalLoadedItems === 0 && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading chat history...
            </Typography>
          </Box>
        )}

        {/* Chat history list - show if we have data or if not loading initially */}
        {!error && (totalLoadedItems > 0 || (!isLoading && totalLoadedItems === 0)) && (
          <List dense sx={{ px: 1 }}>
            {renderHistoryGroup(
              t.get('recentChats'),
              history.recent.filter(i => i.session_id !== currentSessionId),
              <TodayIcon />
            )}
            {renderHistoryGroup(
              t.get('olderChats'),
              history.older.filter(i => i.session_id !== currentSessionId),
              <UpdateIcon />
            )}
            {/* No results message */}
            {totalLoadedItems === 0 && !isLoading && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? t.get('noResults') : t.get('noHistory')}
                </Typography>
                {!searchQuery && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {t.get('startNewConversation')}
                  </Typography>
                )}
              </Box>
            )}
          </List>
        )}

        {/* Infinite scroll loading indicator */}
        {hasMorePages && (
          <Box
            ref={loadingRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: 3,
              minHeight: '80px',
              backgroundColor: 'rgba(0,0,0,0.02)',
              border: '1px dashed rgba(0,0,0,0.1)',
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} thickness={4} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {t.get('loadingHistory')}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  Showing {totalLoadedItems} of {totalItems} conversations
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={loadMore}
                  sx={{
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    minWidth: '120px',
                  }}
                >
                  {t.get('loadMore')}({totalItems - totalLoadedItems})
                </Button>
              </>
            )}
          </Box>
        )}

        {/* End of results indicator */}
        {!hasMorePages && totalLoadedItems > 0 && !isLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              • • •
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t.get('allLoaded')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'absolute',
        top: { xs: 7, sm: 8 },
        left: { xs: 10, sm: 16 },
        zIndex: theme => theme.zIndex.drawer + 1,
      }}
    >
      <IconButton
        color="inherit"
        aria-label="open drawer"
        onClick={toggleDrawer(true)}
        edge="start"
        className={open ? 'drawer-toggle-hidden' : 'drawer-toggle-button'}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="left"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          },
        }}
      >
        {DrawerList}
      </Drawer>
    </Box>
  );
}
